'use client'

import {TabsProps} from 'antd'
import {JWTPayload} from 'jose'
import {Heart} from 'lucide-react'
import {observer, useLocalObservable} from 'mobx-react'

// @ts-ignore
import {useRouter} from 'nextjs-toploader/app'
import React, {useEffect, useRef, useState} from 'react'
import {useFormContext} from 'react-hook-form'
import {toast} from 'react-toastify'
import {z} from 'zod'
import {useMutation} from '@tanstack/react-query'

import {Routes} from '@/src/config/routes'
import {ARTICLE_PROPERTY} from '@/src/config/settings'
import theme from '@/src/config/theme'
import {zFormRules} from '@/src/config/validation'

import {CatalogProductContent} from '@/src/core/domain/entities/catalog'
import {Product, ProductVariant} from '@/src/core/domain/entities/product'

import {useProductViewModel} from '@/src/entities/product'
import {WarehouseAvailable} from '@/src/entities/product/model'

import {FormProvider} from '@/src/shared/modules/form'
import {useFavourite} from '@/src/features/favourite'

import noPhotoPic from '@/src/shared/assets/media/no-photo.png'
import {InvisibleCaptcha, useCaptcha} from '@/src/shared/modules/captcha'
import Button from '@/src/shared/ui/button'
import {LicenseAgreement} from '@/src/shared/ui/form/agreement'
import Gallery from '@/src/shared/ui/gallery'
import FormPhoneInput from '@/src/shared/ui/inputs/form-phone-input'
import {FormInput} from '@/src/shared/ui/inputs/input'
import {AsyncProcessLoading} from '@/src/shared/ui/layout/ui/async-loading'
import {Link} from '@/src/shared/ui/link'
import Modal from '@/src/shared/ui/modal'
import {getClient} from '@/src/shared/utils/api-factory'
import {sanitizeHtml} from '@/src/shared/utils/functions'

import {ProductOffers} from './offers'
import * as UI from './product-detail.styled'
import {TableStyles} from './product-detail.styled'
import {handleMutationException} from '../../../../../web/src/shared/utils/error'
import {isProductViewModelWithVariants} from '@/src/entities/product/helpers/validators'

interface IProps extends CatalogProductContent {
  tabs: TabsProps['items']
}

const FastBuyScheme = z.object({
  last_name: z.string({message: 'Обязательное поле'}).min(1, 'Обязательное поле'),
  name: z.string({message: 'Обязательное поле'}).min(1, 'Обязательное поле'),
  phone: zFormRules.phone,
})

type FastBuyFormData = {
  last_name: string
  name: string
  phone: string
}

const FeedbackNotAvailableScheme = z.object({
  name: z.string({message: 'Обязательное поле'}).min(1, 'Обязательное поле'),
  phone: zFormRules.phone,
  email: z
    .null()
    .optional()
    .or(z.string().email({message: 'Некорректный формат'})),
  message: z.string({message: 'Обязательное поле'}).min(1, 'Обязательное поле'),
})

type FeedbackNotAvailableFormData = {
  name: string
  phone: string
  email?: string
  message: string
}

export const ProductDetailCard = observer(
  ({
    product,
    size_table: sizeTable,
    feedback_block: feedbackBlock,
    delivery_block: deliveryBlock,
    tabs,
    warehouses,
  }: IProps) => {
    const favouriteVm = useFavourite()
    const router = useRouter()
    const sizesTableRef = useRef<Nullable<HTMLDivElement>>(null)

    const productViewModel = useProductViewModel()

    const fastBuyModal = useLocalObservable(() => ({
      open: false,
      show() {
        this.open = true
      },
      close() {
        this.open = false
      },
    }))

    const feedbackNotAvailableModal = useLocalObservable(() => ({
      open: false,
      show() {
        this.open = true
      },
      close() {
        this.open = false
      },
    }))

    const [showSizes, setShowSizes] = useState(false)

    const showSizesTable = () => setShowSizes(true)

    useEffect(() => {
      const intervalId = setInterval(() => {
        if (sizesTableRef.current) {
          if (sizesTableRef.current) {
            const table = sizesTableRef.current!

            // Добавляем обработчики событий для всех ячеек таблицы
            table.addEventListener('mouseover', event => {
              // @ts-ignore TODO: ?
              if (event?.target?.tagName === 'TD') {
                // @ts-ignore TODO: ?
                const index = event.target.cellIndex // Получаем индекс колонки

                // Выделяем все ячейки в этой колонке
                const rows = table.querySelectorAll('tr')
                rows.forEach(row => {
                  const cell = row.cells[index]
                  if (cell) cell.classList.add('highlight')
                })
              }
            })

            table.addEventListener('mouseout', () => {
              // Убираем выделение со всех ячеек
              const highlightedCells = table.querySelectorAll('.highlight')
              highlightedCells.forEach(cell => cell.classList.remove('highlight'))
            })
          }

          clearInterval(intervalId)
        }
      }, 500)
    }, [])

    return (
      <UI.Wrapper
        data-testid="product-detail"
        data-any-offer-id={
          (isProductViewModelWithVariants(productViewModel) &&
            productViewModel.variants.variant?.id) ||
          productViewModel.product.id
        }>
        <UI.Container>
          <UI.TitleWrapper>
            <UI.Title>{product.name}</UI.Title>
            {product.properties && product.properties[ARTICLE_PROPERTY] && (
              <UI.Article>Артикул: {product.properties[ARTICLE_PROPERTY].value}</UI.Article>
            )}
          </UI.TitleWrapper>

          <UI.Badges>
            {(productViewModel.price?.discount_percent ?? 0) > 0 && (
              <UI.SaleInfo>{productViewModel.price?.discount_percent}%</UI.SaleInfo>
            )}
          </UI.Badges>

          <UI.ContentWrapper>
            <UI.Content>
              <UI.Gallery>
                <Gallery
                  images={
                    product.images && product.images.length > 0 ? product.images : [noPhotoPic.src]
                  }
                  preview={product.images && product.images.length > 0}
                />
              </UI.Gallery>

              <UI.Tabs items={tabs} />

              <UI.ServiceRecordLink href={Routes.service.functionalTesting}>
                Запись на тестирование в Лабораторию бега
              </UI.ServiceRecordLink>
            </UI.Content>
            <UI.Sidebar>
              {product.images && product.images?.length > 0 && (
                <UI.Gallery>
                  <Gallery images={product.images} />
                </UI.Gallery>
              )}

              <ProductOffers />

              {sizeTable && (
                <UI.SizesLink $dashed href="" onClick={showSizesTable}>
                  Таблица размеров
                </UI.SizesLink>
              )}

              <UI.WarehouseAvailableContainer>
                {productViewModel
                  .warehouseAvailableList(warehouses)
                  .map((warehouseStock: WarehouseAvailable) => (
                    <UI.WarehouseAvailable key={warehouseStock.id}>
                      <UI.WarehouseLink $dashed href={Routes.contacts.page}>
                        {warehouseStock.title}: {warehouseStock.amount} шт.
                      </UI.WarehouseLink>
                    </UI.WarehouseAvailable>
                  ))}
              </UI.WarehouseAvailableContainer>

              <UI.Prices>
                {productViewModel.price && (
                  <UI.ProductPrice>
                    <span>{productViewModel.price.price_formatted}</span>

                    <UI.FavLink>
                      <Link
                        $dashed
                        asChild
                        onClick={async e => {
                          e.preventDefault()

                          await favouriteVm.toggle(productViewModel.product.id)
                        }}>
                        <Heart
                          color={theme.colors.primary}
                          fill={
                            favouriteVm.is(productViewModel.product.id)
                              ? theme.colors.primary
                              : theme.colors.white
                          }
                        />
                        <span>В избранное</span>
                      </Link>
                    </UI.FavLink>

                    {productViewModel.price.old_price != productViewModel.price.price && (
                      <UI.ProductOldPrice>
                        {productViewModel.price.old_price_formatted}
                      </UI.ProductOldPrice>
                    )}
                  </UI.ProductPrice>
                )}

                {(productViewModel.price && productViewModel.available && (
                  <UI.BuyBtn
                    onClick={async () => {
                      if (productViewModel.inBasket) {
                        router.push(Routes.basket.page)
                      } else {
                        await productViewModel.onClickBuyButton()
                      }
                    }}
                    suppressHydrationWarning>
                    {(productViewModel.inBasket && 'В корзину') || 'Добавить в корзину'}
                  </UI.BuyBtn>
                )) || <UI.LabelNotAvailable>Товара временно нет на складе.</UI.LabelNotAvailable>}
                <UI.Actions>
                  {(productViewModel.price && productViewModel.available && (
                    <></>
                    // <Button secondary onClick={fastBuyModal.show}>
                    //   Быстрая покупка
                    // </Button>
                  )) || (
                    <Button secondary onClick={feedbackNotAvailableModal.show}>
                      Оставить заявку
                    </Button>
                  )}
                </UI.Actions>
                <UI.Actions>
                  <Link $dashed href={Routes.service.shoeTesting}>
                    Записаться на подбор обуви
                  </Link>
                </UI.Actions>
              </UI.Prices>

              {React.createElement(UI.Spoilers, {items: tabs})}

              {feedbackBlock && (
                <UI.ContactUs
                  className="include-area"
                  dangerouslySetInnerHTML={{__html: sanitizeHtml(feedbackBlock)}}
                />
              )}
              {deliveryBlock && (
                <UI.ShipmentInfo
                  className="include-area"
                  dangerouslySetInnerHTML={{__html: sanitizeHtml(deliveryBlock)}}
                />
              )}
            </UI.Sidebar>
          </UI.ContentWrapper>
        </UI.Container>

        {sizeTable && (
          <UI.SizesModal
            as={Modal}
            open={showSizes}
            onCancel={() => setShowSizes(false)}
            centered
            className="sizes-modal">
            <h2>{sizeTable?.title}</h2>

            {sizeTable?.notice && (
              <UI.SizesNotice dangerouslySetInnerHTML={{__html: sanitizeHtml(sizeTable?.notice)}} />
            )}
            {sizeTable?.content && (
              <UI.SizeTable
                ref={sizesTableRef}
                className="zero sizes-chart"
                dangerouslySetInnerHTML={{__html: sanitizeHtml(sizeTable.content)}}
              />
            )}
          </UI.SizesModal>
        )}

        <UI.FeedbackModal
          key={`not-available-${(feedbackNotAvailableModal.open && '+') || '-'}`}
          as={Modal}
          open={feedbackNotAvailableModal.open}
          onCancel={feedbackNotAvailableModal.close}
          title="Оставить заявку">
          <FormProvider schema={FeedbackNotAvailableScheme}>
            <FeedbackNotAvailableForm
              productId={productViewModel.product.id}
              variantId={
                'variants' in productViewModel ? productViewModel.variants.variant?.id : undefined
              }
              onSuccess={feedbackNotAvailableModal.close}
            />
          </FormProvider>
        </UI.FeedbackModal>

        <UI.FeedbackModal
          key={`fast-buy-${(fastBuyModal.open && '+') || '-'}`}
          as={Modal}
          open={fastBuyModal.open}
          onCancel={fastBuyModal.close}
          title="Быстрая покупка">
          <FormProvider schema={FastBuyScheme}>
            <FastBuyForm
              productId={productViewModel.product.id}
              variantId={
                'variants' in productViewModel ? productViewModel.variants?.variant?.id : undefined
              }
              onSuccess={fastBuyModal.close}
            />
          </FormProvider>
        </UI.FeedbackModal>

        <TableStyles />

        {productViewModel.basket?.pending && <AsyncProcessLoading />}
      </UI.Wrapper>
    )
  },
)

interface FastBuyProps {
  productId: Product['id']
  variantId?: ProductVariant['id']
  onSuccess: () => void
}

export const FastBuyForm = ({productId, variantId, onSuccess}: FastBuyProps) => {
  const form = useFormContext<FastBuyFormData>()
  const {token, onSuccessToken, visibleCaptcha, setVisibleCaptcha, renderKey, reset} = useCaptcha()

  const submitMutation = useMutation({
    mutationFn: async (data: FastBuyFormData) => {
      const client = getClient()

      await client.post<Nullable<JWTPayload>>(Routes.webApi.form.product.fastBuy.save, {
        data: {...data, product: {product_id: productId, variant_id: variantId}},
      })
    },

    async onSuccess() {
      toast.success('Форма отправлена')
      onSuccess()
    },
    async onError(error) {
      handleMutationException(error)
      reset()
    },
  })

  const onSubmit = form.handleSubmit(data => {
    submitMutation.mutate(data)
  })

  return (
    <UI.ModalForm>
      <InvisibleCaptcha
        key={renderKey}
        renderKey={renderKey}
        onSuccess={onSuccessToken}
        visible={visibleCaptcha}
        setVisible={setVisibleCaptcha}
      />

      <FormInput name="last_name" required>
        Фамилия
      </FormInput>

      <FormInput name="name" required>
        Имя
      </FormInput>

      <FormPhoneInput name="phone" required>
        Телефон
      </FormPhoneInput>

      <Button onClick={onSubmit} disabled={!token} loading={!token}>
        Купить
      </Button>

      <LicenseAgreement btnName="Купить" />

      {submitMutation.isPending && <AsyncProcessLoading />}
    </UI.ModalForm>
  )
}

interface FeedbackNotAvailableProps {
  productId: Product['id']
  variantId?: ProductVariant['id']
  onSuccess: () => void
}

export const FeedbackNotAvailableForm = ({
  productId,
  variantId,
  onSuccess,
}: FeedbackNotAvailableProps) => {
  const form = useFormContext<FeedbackNotAvailableFormData>()
  const {token, onSuccessToken, visibleCaptcha, setVisibleCaptcha, renderKey, reset} = useCaptcha()

  const submitMutation = useMutation({
    mutationFn: async (data: FeedbackNotAvailableFormData) => {
      const client = getClient()

      await client.post<Nullable<JWTPayload>>(Routes.webApi.form.product.notAvailable.save, {
        data: {...data, product: {product_id: productId, variant_id: variantId}},
      })
    },

    async onSuccess() {
      toast.success('Форма отправлена')
      onSuccess()
    },
    async onError(error) {
      handleMutationException(error)
      reset()
    },
  })

  const onSubmit = form.handleSubmit(data => {
    submitMutation.mutate(data)
  })

  return (
    <UI.ModalForm>
      <InvisibleCaptcha
        key={renderKey}
        renderKey={renderKey}
        onSuccess={onSuccessToken}
        visible={visibleCaptcha}
        setVisible={setVisibleCaptcha}
      />

      <FormInput name="name" required>
        Имя
      </FormInput>

      <FormPhoneInput name="phone" required>
        Телефон
      </FormPhoneInput>

      <FormInput name="email" required>
        Эл. почта
      </FormInput>

      <FormInput name="message" type="textarea">
        Сообщение
      </FormInput>

      <Button onClick={onSubmit} disabled={!token} loading={!token}>
        Отправить
      </Button>

      <LicenseAgreement btnName="Отправить" />

      {submitMutation.isPending && <AsyncProcessLoading />}
    </UI.ModalForm>
  )
}
